/**
 * What the picklist you are ADDING TO already holds.
 *
 * Add Items used to hide any MRS line already on the target picklist. That made an MRS line with
 * quantity still unallocated invisible — `MRS-100026703` line 12563 asked for 4 SET, 2 were on
 * `PL-202609-00238`, and the remaining 2 were reachable only by removing the line and re-adding it
 * (owner report 2026-09-21). Since 2026-09-22 the server tops such a line up instead of refusing it, so
 * the row is shown; these two derivations are what make showing it safe and legible.
 *
 * Pure, and separate from the panel, for the reason `itemPlan.ts` is: the serial/lot distinction in
 * {@link deductHoldings} is the kind of thing that looks right in a component and is wrong for half the
 * catalogue.
 */

/** The subset of `PicklistItemDto` these functions read. */
export interface HeldLine {
  materialRequestItemId: number;
  netsuiteItemId: number;
  quantityToPick: number;
  isVoided: boolean;
  bins: HeldBin[];
}

export interface HeldBin {
  binId: number;
  inventoryNumberId: number | null;
  allocatedQuantity: number;
}

/** The subset of a stock-detail bin row {@link deductHoldings} reads. */
export interface StockRow {
  binId: number;
  inventoryNumberId: number | null;
  quantityAvailable: number;
}

/** One stock row's identity. A null inventory number is a legitimate key: lot items allocate bin-only. */
const unitKey = (binId: number, inventoryNumberId: number | null) =>
  `${binId}:${inventoryNumberId ?? ''}`;

/**
 * How much of each MRS line the picklist already carries, keyed by material request item id.
 *
 * Voided lines are skipped: they hold no quantity, and the server's pending-quantity sum ignores them
 * too — counting one would claim the picklist holds units it has released.
 */
export function heldQuantityByMrsItem(lines: readonly HeldLine[]): Map<number, number> {
  const map = new Map<number, number>();
  for (const line of lines) {
    if (line.isVoided) continue;
    map.set(
      line.materialRequestItemId,
      (map.get(line.materialRequestItemId) ?? 0) + line.quantityToPick
    );
  }
  return map;
}

/**
 * How much the picklist has claimed per NetSuite item, per unit.
 *
 * Keyed by ITEM, not by MRS line: two lines of one item — even across two MRS — draw on the same stock,
 * and the allocation dialog opens for the whole item.
 */
export function claimedQuantityByItem(
  lines: readonly HeldLine[]
): Map<number, Map<string, number>> {
  const byItem = new Map<number, Map<string, number>>();
  for (const line of lines) {
    if (line.isVoided) continue;
    let claimed = byItem.get(line.netsuiteItemId);
    if (!claimed) {
      claimed = new Map<string, number>();
      byItem.set(line.netsuiteItemId, claimed);
    }
    for (const bin of line.bins) {
      const key = unitKey(bin.binId, bin.inventoryNumberId);
      claimed.set(key, (claimed.get(key) ?? 0) + bin.allocatedQuantity);
    }
  }
  return byItem;
}

/**
 * Take what the picklist already holds out of the stock the allocation dialog may draw on.
 *
 * 🪤 Why this is needed at all: the stock read is a NetSuite view and knows nothing about our own local
 * allocations. Without the deduction the dialog offers a serial this picklist is already holding, the
 * user chooses it, and `PicklistBinPool.Reserve` throws on submit — after the work. The pool is right to
 * throw; the dialog was wrong to offer.
 *
 * 🔑 It deducts a QUANTITY rather than dropping the row, because the two tracking kinds need different
 * answers. A serialized item returns one row per unit, so a claim takes the row to zero and it
 * disappears — correct. A lot/untracked row can be 10 units with 3 claimed; dropping it would hide the
 * other 7, which is the same class of bug as hiding the line in the first place.
 */
export function deductHoldings<T extends StockRow>(
  rows: T[],
  claimed: ReadonlyMap<string, number> | undefined
): T[] {
  // ⚡ The SAME array back, not a copy. `BinAllocationDialog` memoises `binsWithStock` on this
  // reference, and the create route — where there is never anything to deduct — calls this on every
  // render of the panel. A fresh array here would bust that memo every time, on the dialog whose tick
  // cost 239ms before it was optimised.
  if (!claimed?.size) return rows;

  const out: T[] = [];
  for (const row of rows) {
    const claimedQty = claimed.get(unitKey(row.binId, row.inventoryNumberId)) ?? 0;
    if (claimedQty <= 0) {
      out.push(row);
      continue;
    }
    const left = row.quantityAvailable - claimedQty;
    if (left > 0) out.push({ ...row, quantityAvailable: left });
  }
  return out;
}
